import { gql } from '@apollo/client';
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';

import { CITIES_FILTER, CITY_FILTER_INTERFACE } from '../../api/cityfilter';
import {
  CITYDATEINTERFACE,
  CITY_PAGE_INTERFACE,
  FETCH_CITY
} from '../../api/cityPage';
import { FEATURED_EXPERIENCES_INTERFACE } from '../../api/featuredexperiences';

import {
  LeftSwipperArrows,
  RightSwipperArrows
} from '../../shared-components/SwipperArrows/SwipperArrows';
import 'swiper/css/navigation';
import { replaceTripshepherdWithSeeSight } from '../../utils/trip-sheppered-text';
import client from '../../apollo-client';
import Trustbar from '../../components/Trust/Trustbar';
import ReviewCard from '../../shared-components/Reviews/ReviewCard';
import dynamic from 'next/dynamic';
import PageHero from '../../layouts/PageHero';
import Head from 'next/head';
import CitiesModal from '../../components/CitiesModal.tsx';
import Image from 'next/image';
import { sourceIcon } from '../../utils/sourceIcon';
import { Rating } from 'react-simple-star-rating';
import { extractFAQs } from '../../utils/extractFAQ';
const FeaturedExperiences = dynamic(
  () => import('../../components/FeaturedExperiences/FeaturedExperiences')
);
const Attractions = dynamic(
  () => import('../../components/CityPage/attractions')
);
const FAQSection = dynamic(() => import('../../shared-components/FAQSection'));
const DetailedReviewModal = dynamic(
  () => import('../../shared-components/testimonials/detailedReviewModal'),
  { ssr: false }
);

const Newsletter = dynamic(() => import('../../layouts/Newsletter/Newsletter'));

const StyledDiv = styled.div`
  margin-top: 2rem;
  font-size: 15px;
  text-align: justify;
  color: #4b5563;

  p {
    margin: 2px 0;
    line-height: 28px;
  }

  h1,
  h2,
  h3,
  h4 {
    position: relative;
  }

  h1 {
    padding-left: 50px;
    font-size: 24px;
  }

  h2 {
    font-size: 18px;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    line-height: 23.4px;
  }

  h4 {
    font-size: 18px;
    font-weight: 500;
    line-height: 25.2px;
  }
  a {
    color: #007be4;
    text-decoration: underline;
  }
`;

const ReviewsContainer = styled.div`
  position: relative;
  padding: 0 60px;
  .swiper {
    padding: 10px 0;
  }
  .swiper-wrapper {
    display: flex !important;
    gap: 10px !important;
  }
  .swiper-slide {
    padding: 0 10px;
    height: auto;
    display: flex;
    align-items: stretch;
  }

  .swiper-button-next,
  .swiper-button-prev {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    cursor: pointer;
  }

  .swiper-button-prev {
    left: 10px;
  }

  .swiper-button-next {
    right: 10px;
  }

  .swiper-button-next::after,
  .swiper-button-prev::after {
    display: none;
  }

  @media (max-width: 1023px) {
    padding: 0 50px;

    .swiper-button-prev {
      left: 5px;
    }

    .swiper-button-next {
      right: 5px;
    }
  }
`;
const NoReviewsMessage = styled.div`
  text-align: center;
  padding: 2rem;
  background: #fafafa;
  border-radius: 8px;
  color: #666;
  font-size: 1.1rem;
  margin: 1rem 0;
`;
interface IPROPS {
  featuredExp: FEATURED_EXPERIENCES_INTERFACE;
  cities: {
    id: string;
    name: string;
    slug: string;
  }[];
  city: CITYDATEINTERFACE;
}

const City = ({ featuredExp, cities, city }: IPROPS) => {
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  const swiperRef = useRef<any>(null);

  const { description = '', faqs = '' } = city?.longDescription
    ? extractFAQs(city.longDescription)
    : { description: '', faqs: '' };

  const updateReviewState = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      const swiperInstance = swiperRef.current.swiper;
      setIsFirstSlide(swiperInstance.isBeginning);
      setIsLastSlide(swiperInstance.isEnd);
    }
  };
  useEffect(() => {
    const handleResize = () => {
      setTimeout(updateReviewState, 200);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Collect reviews and filter valid ones
  const reviews = city?.products?.flatMap(product => product.reviews || []);
  const validReviews = reviews?.filter(
    review => review.published_at && review.review?.trim()?.length > 0
  );
  const sortedReviews = validReviews?.sort((a, b) => {
    const ratingA = Number(a.rating) || 0;
    const ratingB = Number(b.rating) || 0;

    if (ratingA !== ratingB) {
      return ratingB - ratingA;
    }

    return (
      new Date(b.published_at).getTime() - new Date(a.published_at).getTime()
    );
  });
  useEffect(() => {
    if (!validReviews || validReviews.length === 0) return;
    setIsFirstSlide(true);
    setIsLastSlide(false);
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideTo(0);
    }
  }, [city.name]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => {
        if (swiperRef.current && swiperRef.current.swiper) {
          swiperRef.current.swiper.update();
        }
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [sortedReviews, city.name]);
  const handleSeeMore = (review: any) => {
    setSelectedReview(review);
    setShowModal(true);
  };
  const formatReviewerName = (traveller: string | null | undefined) => {
    if (!traveller || traveller.trim() === '') {
      return 'Anonymous';
    }
    return traveller;
  };
  const renderReviewsSection = () => {
    if (!validReviews || validReviews.length === 0) {
      return (
        <div className="w-[90%] lg:w-[85%] 3xl:w-[70%] max-w-[1750px] mx-auto mb-3 lg:mb-5">
          <h2 className="text-[#131313] text-xl font-semibold mt-3">
            Traveller Reviews
          </h2>
          <NoReviewsMessage>
            No reviews available for {city.name}
          </NoReviewsMessage>
        </div>
      );
    }
    return (
      <div className="w-[90%] lg:w-[85%] 3xl:w-[70%] max-w-[1750px] mx-auto mb-3 lg:mb-5 lg:pb-6">
        <h2 className="text-[#131313] text-xl font-semibold mt-3">
          Traveller Reviews
        </h2>
        <div
          id="slider-container"
          className="xxsm:max-h-[450px] lg:max-h-[350px] h-[100%] w-full mt-5 relative"
        >
          <div
            id="slider-grid"
            className="flex justify-between h-[240px] gap-2 sm:gap-5"
          >
            <Swiper
              key={city.name}
              ref={swiperRef}
              slidesPerView="auto"
              spaceBetween={20}
              modules={[Navigation]}
              navigation={{
                nextEl: '.nextBtn',
                prevEl: '.prevBtn'
              }}
              onSlideChange={updateReviewState}
              onReachEnd={() => setIsLastSlide(true)}
              onReachBeginning={() => setIsFirstSlide(true)}
              lazy
              breakpoints={{
                320: {
                  slidesPerView: Math.max(
                    1,
                    Math.min(sortedReviews.length, 1.1)
                  ),
                  spaceBetween: 10
                },
                360: {
                  slidesPerView: Math.max(
                    1,
                    Math.min(sortedReviews.length, 1.2)
                  ),
                  spaceBetween: 10
                },
                640: {
                  slidesPerView: Math.max(1, Math.min(sortedReviews.length, 2)), // Ensure at least 1 slide
                  spaceBetween: 20
                },
                1024: {
                  slidesPerView: Math.max(1, Math.min(sortedReviews.length, 2)),
                  spaceBetween: 20
                },
                1400: {
                  slidesPerView: Math.max(1, Math.min(sortedReviews.length, 3)),
                  spaceBetween: 20
                },
                1600: {
                  slidesPerView: Math.max(1, Math.min(sortedReviews.length, 3)),
                  spaceBetween: 20
                },
                2200: {
                  slidesPerView: Math.max(1, Math.min(sortedReviews.length, 4)),
                  spaceBetween: 20
                }
              }}
              initialSlide={0}
            >
              {sortedReviews?.length > 0 &&
                sortedReviews.map(review => (
                  <SwiperSlide
                    key={review.id}
                    className="rounded-md outline-none overflow-hidden cursor-pointer hover:cursor-default"
                  >
                    <div className="bg-[#FAFAFA] flex flex-col relative py-3 px-5 rounded-md shadow-md h-full">
                      {review?.source ? (
                        <div className="mb-4">
                          <Image
                            src={sourceIcon(review?.source)}
                            width={50}
                            height={50}
                            alt={review?.source}
                            className="absolute top-3 right-3"
                          />
                        </div>
                      ) : (
                        <div className="mb-4">
                          <Image
                            src={sourceIcon('website')}
                            width={50}
                            height={50}
                            alt="reviews"
                            className="absolute top-3 right-3"
                          />
                        </div>
                      )}

                      <div>
                        <Rating
                          initialValue={Number(review?.rating)}
                          disableFillHover={true}
                          allowFraction={true}
                          fillColor="orange"
                          emptyColor="#9B9B9B"
                          readonly={true}
                          SVGstyle={{
                            display: 'inline-block'
                          }}
                          size={15}
                        />
                      </div>
                      <p className="text-sm lg:text-base font-light text-justify mt-1">
                        {review.review?.length > 200 ? (
                          <>
                            {`${review.review.slice(0, 200)}....`}
                            <span
                              className="font-semibold cursor-pointer"
                              onClick={() => handleSeeMore(review)}
                            >
                              see more
                            </span>
                          </>
                        ) : (
                          review.review
                        )}
                      </p>

                      <div className="absolute bottom-3 left-0 right-0 flex justify-between items-center px-6 mt-3">
                        <p className="mr-2 text-sm lg:text-base font-semibold leading-tight whitespace-nowrap">
                          {formatReviewerName(review.traveller)}
                        </p>
                        <span className="text-xs whitespace-nowrap">
                          {new Date(review.published_at).toLocaleDateString(
                            'en-US',
                            {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }
                          )}
                        </span>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>
          <div className="flex justify-center mt-5">
            <div className="flex gap-2 justify-center items-center">
              <div className="prevBtn">
                <LeftSwipperArrows isFirstSlide={isFirstSlide} />
              </div>
              <div className="nextBtn">
                <RightSwipperArrows isLastSlide={isLastSlide} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  return (
    <>
      <Head>
        <meta
          name="og:title"
          content={`${replaceTripshepherdWithSeeSight(city?.pageTitle)}`}
        />
        <meta
          name="og:description"
          content={replaceTripshepherdWithSeeSight(city?.metaDescription)}
          key="ogdescription"
        />
        <title>{`${replaceTripshepherdWithSeeSight(city?.pageTitle)}`}</title>
        <meta
          name="description"
          content={replaceTripshepherdWithSeeSight(city?.metaDescription)}
          key="metadescription"
        />
        <meta
          name="og:image"
          content={
            city?.heroMedia?.length > 0 &&
            !city.heroMedia[0].url.endsWith('.mp4')
              ? city.heroMedia[0].url
              : '/vertical-seesight-logo.png'
          }
        />

        <link
          href={replaceTripshepherdWithSeeSight(city?.canonical)}
          rel="canonical"
          key="canonical"
        />
        <link rel="preload" as="image" href={city.heroMedia[0]?.url} />
      </Head>
      <section id="hero" className="relative">
        <PageHero
          title={city?.header || ''}
          snippet="Size Matters"
          media={city.heroMedia[0]?.url}
          altImage={city.heroMedia[0]?.alt || city.header}
          video={city.heroMedia[0]?.url?.includes('mp4')}
          subheader={city?.subheader || ''}
        />
        <CitiesModal />
      </section>
      <Trustbar />
      <h2 className="w-[90%] lg:w-[85%] 3xl:w-[70%] max-w-[1750px] mx-auto text-xl sm:text-2xl lg:text-3xl font-bold capitalize mt-6">
        Tours in {city?.name}
      </h2>
      {featuredExp ? (
        <FeaturedExperiences
          featuredExp={featuredExp}
          citydropdown={cities}
          selectedCity={city?.name}
        />
      ) : (
        <h2 className="py-10 px-[6%] lg:px-[9%] 3xl:px-[15%] text-xl text-center">
          Tours coming to {city.name} soon
        </h2>
      )}
      <div className="w-[90%] lg:w-[85%] 3xl:w-[70%] max-w-[1750px] mx-auto">
        {city.attractions.length > 1 && (
          <Attractions name={city.name} attractions={city.attractions} />
        )}
      </div>
      {renderReviewsSection()}
      <p className="w-full px-[9%] lg:px-[9%] 3xl:px-[14%] leading-6 text-[15px] mt-8 text-gray-500 text-justify">
        {city.shortDescription}
      </p>

      <div className="px-[6%] lg:px-[7%] 3xl:px-[13%]">
        <section className="py-4 px-4 sm:px-6 lg:px-6">
          <StyledDiv dangerouslySetInnerHTML={{ __html: description }} />
        </section>
        {faqs && (
          <section className="py-4 px-4 sm:px-6 lg:px-8 bg-[#FAFAFA] mb-4">
            <h2 className="text-[20px] leading-[30px] font-semibold tracking-[-1%] mb-8 md:text-[32px] md:leading-[35.2px]">
              FAQs
            </h2>
            <FAQSection content={faqs} />
          </section>
        )}
      </div>
      <Newsletter />
      {showModal && selectedReview && (
        <DetailedReviewModal
          data={{
            //@ts-ignore
            ...selectedReview,
            //@ts-ignore
            traveller: formatReviewerName(selectedReview.traveller)
          }}
          setShowModal={setShowModal}
          productName={city.name}
        />
      )}
    </>
  );
};

export async function getStaticPaths() {
  const { data } = await client.query({
    query: gql`
      query {
        cities {
          id
          name
          slug
        }
      }
    `
  });

  return {
    paths: data.cities.map((city: any) => ({
      params: { slug: city.slug }
    })),
    fallback: false
  };
}
//@ts-ignore
export async function getStaticProps({ params: { slug } }: SLUG) {
  const { data: city } = await client.query<CITY_PAGE_INTERFACE>({
    query: FETCH_CITY,
    variables: {
      slug: slug
    }
  });
  let featuredData: CITY_FILTER_INTERFACE | undefined = undefined;
  try {
    const { data, error } = await client.query<CITY_FILTER_INTERFACE>({
      query: CITIES_FILTER,
      variables: {
        filter: {
          slug: slug
        }
      }
    });
    featuredData = data;
  } catch (error) {}

  if (!featuredData) {
    return {
      props: {
        city: JSON.parse(
          replaceTripshepherdWithSeeSight(JSON.stringify(city.cities[0]))
        )
      }
    };
  }

  if (!city) {
    return { redirect: { destination: '/404', permanent: false } };
  }

  return {
    props: {
      city: JSON.parse(
        replaceTripshepherdWithSeeSight(JSON.stringify(city.cities[0]))
      ),
      featuredExp:
        featuredData &&
        featuredData.cities[0] &&
        JSON.parse(JSON.stringify(featuredData)) &&
        JSON.parse(JSON.stringify(featuredData?.cities[0]))
          ? replaceTripshepherdWithSeeSight(
              JSON.parse(JSON.stringify(featuredData.cities[0]))
            )
          : {},
      cities:
        featuredData &&
        JSON.parse(JSON.stringify(featuredData)) &&
        replaceTripshepherdWithSeeSight(
          JSON.parse(JSON.stringify(featuredData?.citiesList))
        )
    }
  };
}
export default City;
